import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  AbyssViewerComponentProps,
  AbyssViewerContextProvider,
  AbyssViewerNoContext,
  Number3,
  OctreePointCloudProps,
  PointCloudMaterialProps,
  PointPickResult,
  VisibilityBoxMode,
} from '@fabric-web/3d-viewer';
import * as state from '@/components/AssetLabelling/state';
import * as globalState from '@/state';
import { FabricErrorBoundary } from '@/components/shared/FabricErrorBoundary';
import { CuboidClickMode } from '@/types';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useZoneVisibilityBox } from '../hooks';
import { useImageSets, useLabelAnnotationsMaterial } from './hooks';
import { SceneCuboids } from './SceneCuboids';
import { ZoneBoundaries } from './ZoneBoundaries';
import { NeighbouringZones } from './NeighbouringZones';
import { useAddCuboid } from '@/modules/cuboids/hooks/useAddCuboid';
import { useCuboidState } from '../hooks/useCuboidState';
import { useCuboidDataHandlers } from '../hooks/useCuboidDataHandlers';
import { useCuboidStyleHandlers } from '../hooks/useCuboidStyleHandlers';
import { useCuboidVisibilityBox } from '@/modules/cuboids/hooks/useCuboidVisibilityBox';

const CANVAS_PROPS = {
  style: {
    background: '#263238',
  },
  gl: { antialias: true, preserveDrawingBuffer: true },
};

const sphericalImageVisibilityRange: Number3 = [3, 3, 3];

const ViewerInner = () => {
  const structure = useAtomValue(state.structure);
  const selectedZone = useAtomValue(globalState.selectedZone);

  const edlStrength = useAtomValue(globalState.edlStrength);

  const cameraTarget = useAtomValue(globalState.cameraTarget);

  const showViewpoints = useAtomValue(state.showViewpoints);
  const showZoneBoundaries = useAtomValue(state.showZoneBoundaries);
  const showNeighbouringZones = useAtomValue(state.showNeighbouringZones);

  const [viewerCurrentSpherical, setViewerCurrentSpherical] = useAtom(
    globalState.viewerCurrentSpherical
  );
  const [viewerNextCurrentSpherical, setViewerNextCurrentSpherical] = useAtom(
    globalState.viewerNextCurrentSpherical
  );

  const cuboidClickMode = useAtomValue(globalState.cuboidClickMode);

  const [currentVisibilityBox, setCurrentVisibilityBox] = useAtom(globalState.currentVisibilityBox);

  const { setDefaultZoneVisibilityBox } = useZoneVisibilityBox();
  useEffect(() => {
    if (selectedZone) {
      setDefaultZoneVisibilityBox();
    }
  }, [selectedZone, setDefaultZoneVisibilityBox]);

  const { labelAnnotationsMaterial } = useLabelAnnotationsMaterial();

  const pointCloudMaterials = useMemo(
    () =>
      new Map<string, PointCloudMaterialProps>([
        ['annotation', labelAnnotationsMaterial],
        ['color', { isColor: true, attributeName: 'color' }],
      ]),
    [labelAnnotationsMaterial]
  );

  const pointClouds: OctreePointCloudProps[] | undefined = useMemo(() => {
    if (!structure?.octreeResourcesPath) return undefined;
    return [
      {
        name: 'octree-point-cloud',
        url: getCloudfrontUrl(structure?.octreeResourcesPath),
        material: 'annotation',
      },
    ];
  }, [structure?.octreeResourcesPath]);

  const cuboidState = useCuboidState();
  const cuboidDataHandlers = useCuboidDataHandlers();
  const cuboidStyleHandlers = useCuboidStyleHandlers();
  const addCuboid = useAddCuboid(cuboidState, cuboidDataHandlers, cuboidStyleHandlers);

  const onPointCloudClick = useCallback(
    (pickResult: PointPickResult) => {
      if (cuboidClickMode === CuboidClickMode.AddCuboid) {
        const position = pickResult.getPointAttribute(pickResult.pointIndex, 'position');
        if (position && Array.isArray(position) && position.length === 3) {
          addCuboid({ position: position as Number3 });
        }
        return;
      }
      // if (currentCuboid || cuboidClickMode !== CuboidClickMode.Default) {
      //   return;
      // }
      // // Can handle normal pointcloud click here
    },
    [cuboidClickMode, addCuboid]
  );

  const { smallVisibilityBox, setSmallVisibilityBox } = useCuboidVisibilityBox(cuboidState);

  const { sphericalImages, rectilinearImages } = useImageSets();

  const abyssViewerComponentProps: AbyssViewerComponentProps = useMemo(
    () => ({
      cameraTarget,
      pointCloudMaterials,
      pointClouds,
      fileLoaderOptions: { withCredentials: true },
      onPointCloudClick,
      sphericalImageSets: [
        { name: 'Spherical Images', sphericalImages, isVisible: showViewpoints },
      ],
      rectilinearImageSets: [
        { name: 'Rectilinear Images', rectilinearImages, isVisible: showViewpoints },
      ],
      currentImage: viewerCurrentSpherical,
      nextCurrentImage: viewerNextCurrentSpherical,
      setCurrentImage: setViewerCurrentSpherical,
      setNextCurrentImage: setViewerNextCurrentSpherical,
      edlStrength,
      enableEDL: !!edlStrength,
      visibilityBox: currentVisibilityBox,
      setVisibilityBox: setCurrentVisibilityBox,
      smallVisibilityBox,
      setSmallVisibilityBox: smallVisibilityBox ? setSmallVisibilityBox : undefined,
      viewCube: true,
      showPointCloudsInSphericalView: true,
      sphericalImageVisibilityRange,
    }),
    [
      cameraTarget,
      pointCloudMaterials,
      pointClouds,
      onPointCloudClick,
      showViewpoints,
      sphericalImages,
      rectilinearImages,
      viewerCurrentSpherical,
      viewerNextCurrentSpherical,
      setViewerCurrentSpherical,
      setViewerNextCurrentSpherical,
      edlStrength,
      smallVisibilityBox,
      setSmallVisibilityBox,
      currentVisibilityBox,
      setCurrentVisibilityBox,
    ]
  );

  return (
    <AbyssViewerNoContext abyssViewerComponentProps={abyssViewerComponentProps}>
      <SceneCuboids />
      {selectedZone &&
        (showZoneBoundaries || currentVisibilityBox.mode === VisibilityBoxMode.Adjusting) && (
          <ZoneBoundaries zone={selectedZone} />
        )}
      {showNeighbouringZones && <NeighbouringZones />}
    </AbyssViewerNoContext>
  );
};

export const Viewer = () => {
  return (
    <FabricErrorBoundary logToSentry>
      <AbyssViewerContextProvider canvasProps={CANVAS_PROPS}>
        <ViewerInner />
      </AbyssViewerContextProvider>
    </FabricErrorBoundary>
  );
};
